<script setup lang="ts">
import type { BeltColor } from '@@/bits/belt_colors'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import { computed } from 'vue'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import { NATIVE_HOST } from '@@/bits/url'

const props = defineProps<{
  color: BeltColor
  isDarkMode: boolean
}>()

const LOGO_IMAGE_WIDTH = 65
const LOGO_IMAGE_HEIGHT = 21

const LOGO_LINK_URL = `https://${NATIVE_HOST}`

const padletLogoForBeltColorUrl = computed(() => {
  if (props.color === 'black') {
    // Black logo is the same in both light and dark mode
    return `https://padlet.net/dashboard/belt/v1_profile_padlet_logo_black.svg`
  }
  return `https://padlet.net/dashboard/belt/v1_profile_padlet_logo_${props.color}_${props.isDarkMode ? 'dark' : 'light'}.svg`
})
</script>

<template>
  <OzBaseButton
    v-if="color !== 'white'"
    :class="['z-10', 'relative', 'group', 'rounded-lg', 'bg-transparent']"
    data-testid="padletLogo"
    :href="LOGO_LINK_URL"
  >
    <!-- Handle a11y states in an absolutely positioned element so that we don't affect the position of the logo image -->
    <div
      :class="[
        'absolute',
        '-inset-x-2 -inset-y-1',
        'rounded-lg',
        // Hover
        {
          'hover-hover:group-hover:bg-button-dark': props.color === 'black',
          'hover-hover:group-hover:bg-button-light hover-hover:dark:group-hover:bg-button-dark':
            props.color !== 'black',
        },
        // Active
        'hover-hover:group-active:bg-button-hover-light hover-hover:dark:group-active:bg-button-hover-dark',
        // Focus
        'group-focus-visible:ring-2',
        {
          'group-focus-visible:ring-canary-500': props.color === 'black',
          'group-focus-visible:ring-grape-500 dark:group-focus-visible:ring-canary-500': props.color !== 'black',
        },
      ]"
    />
    <ImageThumbnail
      :src="padletLogoForBeltColorUrl"
      :original-image-width="LOGO_IMAGE_WIDTH"
      :original-image-height="LOGO_IMAGE_HEIGHT"
      :width="LOGO_IMAGE_WIDTH"
      :height="LOGO_IMAGE_HEIGHT"
      alt="Padlet logo"
      class="relative z-10"
    />
  </OzBaseButton>
</template>
